import React, { useState, useEffect, useRef } from "react"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import { Link } from "react-router-dom"

const AddCollection = () => {
  const formRef = useRef(null)
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [ratechartType, setRatechartType] = useState("")
  const [selectedPaymentMode, setSelectedPaymentMode] = useState("")
  const [companies, setCompanies] = useState([])
  const [plants, setPlants] = useState([])
  const [chCenters, setChCenters] = useState([])
  const [routes, setRoutes] = useState([])
  const [states, setStates] = useState([])
  const [districts, setDistricts] = useState([])
  const [taluks, setTaluks] = useState([])
  const [selectedCompany, setSelectedCompany] = useState("")
  const [selectedPlant, setSelectedPlant] = useState("")
  const [selectedCC, setSelectedCC] = useState("")
  const [loadingCC, setLoadingCC] = useState(false)
  const [loadingRoutes, setLoadingRoutes] = useState(false)
  const [loadingDistricts, setLoadingDistricts] = useState(false)
  const [loadingTaluks, setLoadingTaluks] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedPanImage, setSelectedPanImage] = useState(null)
  const [selectedBankImage, setSelectedBankImage] = useState(null)
  const [applicableMilk, setApplicableMilk] = useState({ cow: true, buffalo: true })

  useEffect(() => {
    const load = async () => {
      try {
        const [compResp, plantResp, stResp] = await Promise.all([
          api.get("companies"), api.get("plants"), api.get("states")
        ])
        const compData = Array.isArray(compResp.data.data) ? compResp.data.data : compResp.data.data?.data || []
        setCompanies(compData)
        if (compData.length === 1) setSelectedCompany(String(compData[0].id))
        setPlants(Array.isArray(plantResp.data.data) ? plantResp.data.data : plantResp.data.data?.data || [])
        setStates(Array.isArray(stResp.data.data) ? stResp.data.data : stResp.data.data?.data || [])
      } catch (e) { console.error(e) }
    }
    load()
  }, [])

  const hasMultiCompanies = companies.length > 1
  const filteredPlants = hasMultiCompanies && selectedCompany
    ? plants.filter(p => String(p.company_id) === selectedCompany)
    : plants

  // Load CCs when plant changes
  const handlePlantChange = async (plantId) => {
    setSelectedPlant(plantId)
    setChCenters([]); setRoutes([]); setSelectedCC("")
    if (!plantId) return
    setLoadingCC(true)
    try {
      const resp = await api.get("chillings-by-plant/" + plantId)
      setChCenters(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
    setLoadingCC(false)
  }

  // Load routes when CC changes
  const handleCCChange = async (ccId) => {
    setSelectedCC(ccId)
    setRoutes([])
    if (!ccId) return
    setLoadingRoutes(true)
    try {
      const resp = await api.get("route-cc/" + ccId)
      setRoutes(Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || [])
    } catch (e) { console.error(e) }
    setLoadingRoutes(false)
  }

  const stateChangeHandler = async e => {
    const stateId = e.target.value; setDistricts([]); setTaluks([])
    if (!stateId) return; setLoadingDistricts(true)
    try { const r = await api.get("districts/" + stateId); setDistricts(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch (e) { console.error(e) }
    setLoadingDistricts(false)
  }
  const districtChangeHandler = async e => {
    const districtId = e.target.value; setTaluks([])
    if (!districtId) return; setLoadingTaluks(true)
    try { const r = await api.get("taluks/" + districtId); setTaluks(Array.isArray(r.data.data) ? r.data.data : r.data.data?.data || []) } catch (e) { console.error(e) }
    setLoadingTaluks(false)
  }

  const handleFileRead = (e, setter) => {
    const file = e.target.files[0]
    if (file) { const r = new FileReader(); r.onloadend = () => setter(r.result); r.readAsDataURL(file) }
    else setter(null)
  }

  const handleValidSubmit = async (event, values) => {
    setLoading(true)
    try {
      values.adhaar_upload = selectedImage
      values.pan_upload = selectedPanImage
      values.bank_upload = selectedBankImage
      // Build applicable milk types string
      const milkArr = []
      if (applicableMilk.cow) milkArr.push("Cow")
      if (applicableMilk.buffalo) milkArr.push("Buffalo")
      values.applicableMilkTypes = milkArr.join(",")
      // Remove UI filter fields — not DB columns
      delete values.plant_filter
      delete values.company_filter
      const resp = await api.post("create-collection-center", values)
      if (resp.data.success) {
        formRef.current.reset()
        setSubmitted(true); setDistricts([]); setTaluks([]); setRatechartType("")
        setChCenters([]); setRoutes([]); setSelectedPlant(""); setSelectedCC("")
        if (hasMultiCompanies) setSelectedCompany("")
        setApplicableMilk({ cow: true, buffalo: true })
        setTimeout(() => setSubmitted(false), 4000)
      }
    } catch (error) {
      console.error(error)
      alert(error.response?.data?.message || error.response?.data?.error || "Failed to add collection center")
    }
    setLoading(false)
  }

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Add Collection Center</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}>
            <Link to="/collection-center-vlcc" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link>
          </Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm ref={formRef} className="form-horizontal mt-4" onValidSubmit={handleValidSubmit}>

          {/* Center Info */}
          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-store-alt"></i> Center Information
          </h6>
          <Row>
            {hasMultiCompanies && (
              <Col md={3}><div className="mb-3">
                <AvField type="select" name="company_filter" label="Company *"
                  onChange={e => { setSelectedCompany(e.target.value); setSelectedPlant(""); setChCenters([]); setRoutes([]); setSelectedCC("") }}>
                  <option value="">Select Company</option>
                  {companies.map((c, i) => <option key={i} value={String(c.id)}>{c.name}</option>)}
                </AvField>
              </div></Col>
            )}
            <Col md={hasMultiCompanies ? 2 : 3}><div className="mb-3">
              <AvField type="select" name="plant_filter" label="Plant *"
                onChange={e => handlePlantChange(e.target.value)}>
                <option value="">{hasMultiCompanies && !selectedCompany ? "Select Company first" : "Select"}</option>
                {filteredPlants.map((p, i) => <option key={i} value={String(p.id)}>{p.name}</option>)}
              </AvField>
            </div></Col>
            <Col md={hasMultiCompanies ? 2 : 3}><div className="mb-3">
              <AvField type="select" name="chillingCenter" label={<>Chilling Center * {loadingCC && <Spinner size="sm" color="primary" className="ms-1" />}</>} required
                onChange={e => handleCCChange(e.target.value)}>
                <option value="">{loadingCC ? "Loading..." : chCenters.length ? "Select" : "Select Plant first"}</option>
                {chCenters.map((c, i) => <option key={i} value={String(c.id)}>{c.name}{c.number ? ` (${c.number})` : ""}</option>)}
              </AvField>
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField type="select" name="route" label={<>Route * {loadingRoutes && <Spinner size="sm" color="primary" className="ms-1" />}</>} required>
                <option value="">{loadingRoutes ? "Loading..." : routes.length ? "Select" : "Select CC first"}</option>
                {routes.map((r, i) => <option key={i} value={String(r.id)}>{r.routeName}{r.routeNumber ? ` (${r.routeNumber})` : ""}</option>)}
              </AvField>
            </div></Col>
            <Col md={3}><div className="mb-3">
              <AvField type="select" name="operationType" label="Operation Type *" required>
                <option value="">Select</option>
                <option value="Direct Company Center">Direct Company Center</option>
                <option value="Agent Center">Agent Center</option>
                <option value="Independent Center">Independent Center</option>
              </AvField>
            </div></Col>
          </Row>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="centerName" label="Center Name *" type="text" required placeholder="Enter Center Name" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="centerNumber" label="Center Number *" type="text" required placeholder="Enter Center Number" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="canNumber" label="Can Number" type="text" placeholder="Enter Can Number" /></div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="virtualCenter" label="Is this VLCC a Virtual Center?" errorMessage="Pick one!">
                <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
              </AvRadioGroup>
            </div></Col>
          </Row>

          {/* Address */}
          <h6 style={{ color: "#0ea5e9", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-map-marker-alt"></i> Address Details
          </h6>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="door" label="Door No" type="text" placeholder="Door No" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="village" label="Village Name" type="text" placeholder="Village Name" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="taluk" label="Taluk Name" type="text" placeholder="Taluk Name" /></div></Col>
          </Row>
          <Row>
            <Col md={4}><div className="mb-3">
              <AvField type="select" name="state" label="State" onChange={stateChangeHandler}>
                <option value="">Select State</option>
                {states.map((s, i) => <option key={i} value={String(s.id)}>{s.state}</option>)}
              </AvField>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField type="select" name="district" label={<>District {loadingDistricts && <Spinner size="sm" color="primary" className="ms-1" />}</>} onChange={districtChangeHandler}>
                <option value="">{loadingDistricts ? "Loading..." : districts.length ? "Select District" : "Select State first"}</option>
                {districts.map((d, i) => <option key={i} value={String(d.id)}>{d.district}</option>)}
              </AvField>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvField name="pincode" label="Pincode" type="text" placeholder="e.g. 388001"
                validate={{ pattern: { value: /^[1-9][0-9]{5}$/, errorMessage: "Valid 6-digit pin" },
                  minLength: { value: 6, errorMessage: "6 digits" }, maxLength: { value: 6, errorMessage: "6 digits" } }} />
            </div></Col>
          </Row>

          {/* Incharge/People */}
          <h6 style={{ color: "#22c55e", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-user-tie"></i> People & Management
          </h6>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="inchargeName" label="Center Incharge Name" type="text" placeholder="Incharge Name" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="inchargeMobile" label="Center Incharge Mobile" type="text" placeholder="Mobile Number" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="inchargeEmail" label="Center Incharge Email" type="email" placeholder="Email Address" /></div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3"><AvField name="inchargeAadhar" label="Center Incharge Aadhaar" type="text" placeholder="Aadhar Number" /></div></Col>
            <Col md={6}><div className="mb-3"><AvField name="inchargePAN" label="Center Incharge PAN" type="text" placeholder="PAN Number" /></div></Col>
          </Row>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="supervisor" label="Supervisor" type="text" placeholder="Supervisor Name" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="secretaryName" label="Secretary Name" type="text" placeholder="Secretary Name" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="secretaryMobile" label="Secretary Mobile" type="text" placeholder="Mobile" /></div></Col>
          </Row>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="presidentName" label="President Name" type="text" placeholder="President Name" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="presidentMobile" label="President Mobile" type="text" placeholder="Mobile" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="remarks" label="Remarks" type="textarea" rows="2" placeholder="Any remarks..." /></div></Col>
          </Row>

          {/* Rate Chart & Settings */}
          <h6 style={{ color: "#f59e0b", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-chart-line"></i> Rate Chart & Settings
          </h6>
          <Row>
            <Col md={4}><div className="mb-3">
              <AvRadioGroup inline name="rateChartType" label="Rate Chart Type *" required errorMessage="Pick one!" onChange={e => setRatechartType(e.target.value)}>
                <AvRadio label="Common" value="0" /><AvRadio label="Separate" value="1" />
              </AvRadioGroup>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvRadioGroup inline name="paymentMode" label="Payment Mode *" required errorMessage="Pick one!" onChange={e => setSelectedPaymentMode(e.target.value)}>
                <AvRadio label="Cash" value="0" /><AvRadio label="Bank Account" value="1" />
              </AvRadioGroup>
            </div></Col>
          </Row>
          {selectedPaymentMode === "1" && (
            <Row>
              <Col md={4}><div className="mb-3">
                <AvField name="bankAccountNumber" label="Bank Account Number *" type="text" required placeholder="Enter Account Number" />
              </div></Col>
              <Col md={4}><div className="mb-3">
                <AvField name="bankAccountName" label="Bank Account Name *" type="text" required placeholder="Enter Account Name" />
              </div></Col>
              <Col md={4}><div className="mb-3">
                <AvRadioGroup inline name="bankAccountType" label="Bank Account Type *" required errorMessage="Pick one!">
                  <AvRadio label="Savings" value="Savings" /><AvRadio label="Current" value="Current" />
                </AvRadioGroup>
              </div></Col>
            </Row>
          )}

          {/* Document Uploads */}
          <h6 style={{ color: "#ef4444", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-file-upload"></i> Document Uploads
          </h6>
          <Row>
            <Col md={4}>
              <label className="form-label">Upload Aadhaar Copy</label>
              <input type="file" className="form-control" accept="image/*" onChange={e => handleFileRead(e, setSelectedImage)} />
              {selectedImage && <img src={selectedImage} alt="Aadhar" style={{ maxWidth: "150px", maxHeight: "100px", marginTop: "8px", borderRadius: "4px" }} />}
            </Col>
            <Col md={4}>
              <label className="form-label">Upload PAN Copy</label>
              <input type="file" className="form-control" accept="image/*" onChange={e => handleFileRead(e, setSelectedPanImage)} />
              {selectedPanImage && <img src={selectedPanImage} alt="PAN" style={{ maxWidth: "150px", maxHeight: "100px", marginTop: "8px", borderRadius: "4px" }} />}
            </Col>
            <Col md={4}>
              <label className="form-label">Upload Bank Passbook/Statement</label>
              <input type="file" className="form-control" accept="image/*" onChange={e => handleFileRead(e, setSelectedBankImage)} />
              {selectedBankImage && <img src={selectedBankImage} alt="Bank" style={{ maxWidth: "150px", maxHeight: "100px", marginTop: "8px", borderRadius: "4px" }} />}
            </Col>
          </Row>

          {/* CC Purchase Settings */}
          <h6 style={{ color: "#8b5cf6", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "20px" }}>
            <i className="fas fa-cogs"></i> CC Purchase Settings
          </h6>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="agentCommissionCowMilk" label="Agent Commission Available for Cow Milk in CC Purchase?" errorMessage="Pick one!">
                <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
              </AvRadioGroup>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="agentCommissionBuffaloMilk" label="Agent Commission Available for Buffalo Milk in CC Purchase?" errorMessage="Pick one!">
                <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
              </AvRadioGroup>
            </div></Col>
          </Row>
          <Row>
            <Col md={6}><div className="mb-3">
              <AvField type="select" name="pickupVehicle" label="Pickup Vehicle">
                <option value="">Select</option>
                <option value="Auto">Auto</option>
                <option value="Van">Van</option>
                <option value="Truck">Truck</option>
                <option value="Bike">Bike</option>
                <option value="Other">Other</option>
              </AvField>
            </div></Col>
            <Col md={6}><div className="mb-3">
              <AvRadioGroup inline name="limitSingleEntryVendorCC" label="Limit Single Entry by Milk Type for this vendor in CC *" required errorMessage="Pick one!">
                <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
              </AvRadioGroup>
            </div></Col>
          </Row>
          <Row>
            <Col md={4}><div className="mb-3">
              <label className="form-label">Applicable Milk Types in CC *</label>
              <div style={{ display: "flex", gap: "16px", paddingTop: "6px" }}>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="milkCow" checked={applicableMilk.cow}
                    onChange={e => setApplicableMilk(p => ({ ...p, cow: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="milkCow">Cow</label>
                </div>
                <div className="form-check">
                  <input className="form-check-input" type="checkbox" id="milkBuffalo" checked={applicableMilk.buffalo}
                    onChange={e => setApplicableMilk(p => ({ ...p, buffalo: e.target.checked }))} />
                  <label className="form-check-label" htmlFor="milkBuffalo">Buffalo</label>
                </div>
              </div>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvRadioGroup inline name="defaultMilkTypeCC" label="Default Milk Type in CC" errorMessage="Pick one!">
                <AvRadio label="Cow" value="1" /><AvRadio label="Buffalo" value="2" /><AvRadio label="None" value="0" />
              </AvRadioGroup>
            </div></Col>
            <Col md={4}><div className="mb-3">
              <AvRadioGroup inline name="sendSmsCcPurchases" label="Send SMS for CC Purchases *" required errorMessage="Pick one!">
                <AvRadio label="Yes" value="1" /><AvRadio label="No" value="0" />
              </AvRadioGroup>
            </div></Col>
          </Row>

          {/* Export Parameters */}
          <h6 style={{ color: "#64748b", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px", marginTop: "12px" }}>
            <i className="fas fa-file-export"></i> Export Parameters
          </h6>
          <Row>
            <Col md={4}><div className="mb-3"><AvField name="parameter1" label="Export Parameter 1" type="text" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="parameter2" label="Export Parameter 2" type="text" /></div></Col>
            <Col md={4}><div className="mb-3"><AvField name="parameter3" label="Export Parameter 3" type="text" /></div></Col>
          </Row>

          <div className="mb-3 row mt-4">
            <div className="col-6">
              {submitted && <UncontrolledAlert color="success"><strong>Collection Center Added!</strong></UncontrolledAlert>}
            </div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading}>
                {loading ? <><Spinner size="sm" /><span style={{ paddingLeft: "10px" }}>Adding...</span></> : <span><i className="fas fa-plus-circle"></i> Add Center</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default AddCollection
